import { labelize } from '@buyside/util/arrayHelper';

export const directionList = [
  { value: '', label: 'All' },
  { value: 'long', label: 'Longs' },
  { value: 'short', label: 'Shorts' },
  { value: 'pair', label: 'Pair Trades' }
];

export const fundamentals: {
  name: string;
  label: string;
}[] = [
  { name: 'market_cap', label: 'Market Cap (USD)' },
  { name: 'usd_ev', label: 'EV (USD)' },
  { name: 'volume', label: 'Three Mo. ADV (USD)' },
  { name: 'sellside_consensus', label: 'Sellside Consensus' }
];

export enum fundamentalNames {
  MARKET_CAP = 'market_cap',
  USD_EV = 'usd_ev',
  VOLUME = 'volume',
  SELLSIDE_CONSENSUS = 'sellside_consensus'
}

export const multiplesRatios = [
  { name: 'current_pe', label: 'Current P/E' },
  { name: 'forward_pe', label: 'Forward P/E' },
  { name: 'ev_ltm_ebitda', label: 'EV/LTM EBITDA' },
  { name: 'ev_fcf', label: 'EV/LTM FCF' },
  { name: 'fcf_yield', label: 'FCF Yield' },
  { name: 'div_yield', label: 'Dividend Yield' }
];

export const sliders: {
  [key: string]: { increments: (number | string)[]; ticks: number[] | string[] };
} = {
  aum_ranges: {
    increments: [
      0,
      10000000,
      20000000,
      30000000,
      40000000,
      50000000,
      100000000,
      150000000,
      200000000,
      250000000,
      300000000,
      500000000,
      750000000,
      1000000000,
      2000000000,
      3500000000,
      5000000000,
      7500000000,
      10000000000,
      50000000000,
      75000000000,
      100000000000,
      150000000000,
      200000000000
    ],
    ticks: [0, 40000000, 250000000, 2000000000, 10000000000, 200000000000]
  },
  sellside_consensus: {
    increments: ['Buy', 'Overweight', 'Hold', 'Underweight', 'Sell'],
    ticks: ['Buy', 'Hold', 'Sell']
  },
  fcf_yield: {
    increments: [
      0,
      0.01,
      0.02,
      0.03,
      0.04,
      0.05,
      0.06,
      0.07,
      0.08,
      0.09,
      0.1,
      0.11,
      0.12,
      0.13,
      0.14,
      0.15,
      0.16,
      0.17,
      0.18,
      0.19,
      0.2
    ],
    ticks: [0, 0.05, 0.1, 0.15, 0.2]
  },
  forward_pe: {
    increments: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 12, 14, 16, 18, 20, 25, 30, 40, 45, 50],
    ticks: [0, 5, 10, 20, 50]
  },
  ev_fcf: {
    increments: [
      -25,
      -23,
      -21,
      -19,
      -17,
      -15,
      -13,
      -11,
      -9,
      -7,
      -5,
      -3,
      -1,
      1,
      3,
      5,
      7,
      9,
      11,
      13,
      15,
      17,
      19,
      21,
      23,
      25
    ],
    ticks: [-25, -15, -5, 5, 15, 25]
  },
  returns: {
    increments: [
      -50,
      -44,
      -38,
      -33,
      -27,
      -21,
      -15,
      -10,
      -8,
      -7,
      -5,
      -4,
      -2,
      -1,
      0,
      1,
      2,
      4,
      5,
      7,
      8,
      10,
      15,
      21,
      27,
      33,
      38,
      44,
      50
    ],
    ticks: [-50, -10, 0, 10, 50]
  },
  market_cap: {
    increments: [
      0 /* nano & micro */,
      50_000_000,
      100_000_000,
      200_000_000,
      300_000_000 /* small */,
      500_000_000,
      750_000_000,
      1_000_000_000,
      2_000_000_000 /* mid */,
      3_000_000_000,
      5_000_000_000,
      10_000_000_000 /* large */,
      20_000_000_000,
      50_000_000_000,
      100_000_000_000,
      200_000_000_000 /* mega */,
      4_000_000_000_000,
      8_000_000_000_000
    ],
    ticks: [0, 300_000_000, 2_000_000_000, 10_000_000_000, 200_000_000_000, 8_000_000_000_000]
  },
  volume: {
    increments: [
      0,
      10000,
      50000,
      100000,
      250000,
      500000,
      750000,
      1000000,
      1500000,
      2000000,
      3000000,
      4000000,
      5000000,
      7500000,
      10000000,
      15000000,
      20000000,
      25000000,
      30000000,
      40000000,
      50000000,
      75000000,
      150000000,
      250000000,
      750000000,
      1500000000
    ],
    ticks: [0, 500000, 3000000, 15000000, 50000000, 1500000000]
  },
  ev: {
    increments: [
      -1000000000,
      -500000000,
      -250000000,
      0,
      10000000,
      20000000,
      30000000,
      40000000,
      50000000,
      100000000,
      150000000,
      200000000,
      250000000,
      300000000,
      500000000,
      750000000,
      1000000000,
      2000000000,
      3500000000,
      5000000000,
      7500000000,
      10000000000,
      50000000000,
      75000000000,
      100000000000,
      150000000000,
      200000000000
    ],
    ticks: [-1000000000, 0, 40000000, 250000000, 2000000000, 10000000000, 200000000000]
  },
  usd_ev: {
    increments: [
      -1000000000,
      -500000000,
      -250000000,
      0,
      10000000,
      20000000,
      30000000,
      40000000,
      50000000,
      100000000,
      150000000,
      200000000,
      250000000,
      300000000,
      500000000,
      750000000,
      1000000000,
      2000000000,
      3500000000,
      5000000000,
      7500000000,
      10000000000,
      50000000000,
      75000000000,
      100000000000,
      150000000000,
      200000000000
    ],
    ticks: [-1000000000, 0, 40000000, 250000000, 2000000000, 10000000000, 200000000000]
  },
  gpa: {
    increments: [0, 0.5, 1.0, 1.5, 2.0, 2.5, 3.0, 3.5, 4.0],
    ticks: [0, 1.0, 2.0, 3.0, 4.0]
  },
  sat: {
    increments: [0, 400, 800, 1200, 1400, 1600, 1800, 2000, 2200, 2400],
    ticks: [0, 1200, 1600, 2000, 2400]
  },
  act: {
    increments: [0, 16, 20, 22, 24, 26, 28, 30, 32, 33, 34, 35, 36],
    ticks: [0, 20, 26, 32, 36]
  },
  years_of_experience: {
    increments: [0, 2, 3, 4, 5, 6, 7, 8, 9, 10, 12, 14, 16, 18, 20],
    ticks: [0, 2, 5, 10, 20]
  },
  ltm_revenue: {
    increments: [
      0,
      10000000,
      20000000,
      30000000,
      40000000,
      50000000,
      100000000,
      150000000,
      200000000,
      250000000,
      300000000,
      500000000,
      750000000,
      1000000000,
      2000000000,
      3500000000,
      5000000000,
      7500000000,
      10000000000,
      50000000000,
      75000000000,
      100000000000,
      150000000000,
      200000000000
    ],
    ticks: [0, 40000000, 250000000, 2000000000, 10000000000, 200000000000]
  },
  current_pe: {
    increments: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 12, 14, 16, 18, 20, 25, 30, 40, 45, 50],
    ticks: [0, 5, 10, 20, 50]
  },
  fy1_sellside_consensus_pe: {
    increments: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 12, 14, 16, 18, 20, 25, 30, 40, 45, 50],
    ticks: [0, 5, 10, 20, 50]
  },
  div_yield: {
    increments: [
      0,
      0.5,
      1,
      1.5,
      2,
      2.5,
      3,
      3.5,
      4,
      4.5,
      5,
      6,
      7,
      8,
      9,
      10,
      11,
      12,
      13,
      14,
      15,
      16,
      17,
      18,
      19,
      20
    ],
    ticks: [0, 2.5, 5, 10, 15, 20]
  },
  ev_ltm_revenue: {
    increments: [
      -25,
      -23,
      -21,
      -19,
      -17,
      -15,
      -13,
      -11,
      -9,
      -7,
      -5,
      -3,
      -1,
      1,
      3,
      5,
      7,
      9,
      11,
      13,
      15,
      17,
      19,
      21,
      23,
      25
    ],
    ticks: [-25, -15, -5, 5, 15, 25]
  },
  ev_ltm_ebitda: {
    increments: [
      -25,
      -23,
      -21,
      -19,
      -17,
      -15,
      -13,
      -11,
      -9,
      -7,
      -5,
      -3,
      -1,
      1,
      3,
      5,
      7,
      9,
      11,
      13,
      15,
      17,
      19,
      21,
      23,
      25
    ],
    ticks: [-25, -15, -5, 5, 15, 25]
  },
  to_100: {
    increments: [
      0,
      4,
      8,
      12,
      16,
      20,
      24,
      28,
      32,
      36,
      40,
      44,
      48,
      52,
      56,
      60,
      64,
      68,
      72,
      76,
      80,
      84,
      88,
      92,
      96,
      100
    ],
    ticks: [0, 20, 40, 60, 80, 100]
  },
  discount_52_week_high: {
    increments: [
      0,
      4,
      8,
      12,
      16,
      20,
      24,
      28,
      32,
      36,
      40,
      44,
      48,
      52,
      56,
      60,
      64,
      68,
      72,
      76,
      80,
      84,
      88,
      92,
      96,
      100
    ],
    ticks: [0, 20, 40, 60, 80, 100]
  },
  premium_52_week_low: {
    increments: [
      0,
      4,
      8,
      12,
      16,
      20,
      24,
      28,
      32,
      36,
      40,
      44,
      48,
      52,
      56,
      60,
      64,
      68,
      72,
      76,
      80,
      84,
      88,
      92,
      96,
      100
    ],
    ticks: [0, 20, 40, 60, 80, 100]
  },
  standard_credit_rating: {
    increments: [
      'AAA',
      'AA+',
      'AA',
      'AA-',
      'A+',
      'A',
      'A-',
      'BBB+',
      'BBB',
      'BBB-',
      'BB+',
      'BB',
      'BB-',
      'B+',
      'B',
      'B-',
      'CCC+',
      'CCC',
      'CCC-',
      'CC',
      'C',
      'D',
      'NR'
    ],
    ticks: ['AAA', 'A+', 'BBB', 'BB', 'B', 'CCC', 'C', 'NR']
  },
  moody_credit_rating: {
    increments: [
      'Aaa',
      'Aa1',
      'Aa2',
      'A1',
      'A2',
      'A3',
      'Baa1',
      'Baa2',
      'Baa3',
      'Ba1',
      'Ba2',
      'Ba3',
      'B1',
      'B2',
      'B3',
      'Caa1',
      'Caa2',
      'Ca',
      'C',
      'NR'
    ],
    ticks: ['Aaa', 'A3', 'Baa3', 'Ba3', 'B3', 'Ca', 'NR']
  },
  fitch_credit_rating: {
    increments: [
      'AAA',
      'AA+',
      'AA',
      'AA-',
      'A+',
      'A',
      'A-',
      'BBB+',
      'BBB',
      'BBB-',
      'BB+',
      'BB',
      'BB-',
      'B+',
      'B',
      'B-',
      'CCC+',
      'CC',
      'C',
      'RD',
      'D',
      'NR'
    ],
    ticks: ['AAA', 'A', 'BBB', 'BB', 'B', 'CCC+', 'C', 'NR']
  },
  amount_outstanding: {
    increments: [
      0,
      10000000,
      20000000,
      30000000,
      40000000,
      50000000,
      100000000,
      150000000,
      200000000,
      250000000,
      300000000,
      500000000,
      750000000,
      1000000000,
      2000000000,
      3500000000,
      5000000000,
      7500000000,
      10000000000,
      50000000000,
      75000000000,
      100000000000,
      150000000000,
      200000000000
    ],
    ticks: [0, 40000000, 250000000, 2000000000, 10000000000, 200000000000]
  }
};

export const userRatingsCount = [
  { value: '0_4', label: '0-4' },
  { value: '5_9', label: '5-9' },
  { value: '10_', label: '10+' }
];

export const communityRatings = [0, 1, 2, 3, 4, 5];
export const communityRatingIncrements = [0, 0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5];
// community rating increments copied over from old IdeaFilter -sw 7/12

export const minExpectedReturn = [0, 20, 40, 60, 80, 100];

export const performanceLabels = [
  { label: 'Return To Date', name: 'total_return' },
  { label: 'Return vs Benchmark', name: 'return_vs_benchmark' },
  { label: 'IRR', name: 'annualized_return' }
];

export const engagement = {
  views: [
    { value: 'high', label: 'High' },
    { value: 'average', label: 'Average' },
    { value: 'low', label: 'Low' },
    { value: 'none', label: 'None' }
  ],
  numberOfComments: [
    { value: '0_4', label: '0-4' },
    { value: '5_9', label: '5-9' },
    { value: '10_', label: '10+' }
  ]
};

export const timeframes = [
  { value: 'less_than_3_months', label: '< 3 Months' },
  { value: '3_months_to_6_months', label: '3-6 Months' },
  { value: '6_months_to_1_year', label: '6 Mo. - 1 Year' },
  { value: '1_year_to_2_years', label: '1-2 Years' },
  { value: '2_years_to_5_years', label: '2-5 Years' },
  { value: '5_years_to_7_years', label: '5-7 Years' },
  { value: '7_years_to_10_years', label: '7-10 Years' },
  { value: '10_years_to_12_years', label: '10-12 Years' }
];

export const portfolioPercentages = [
  { value: 'Less than 0.1%', label: 'Less than 0.1%' },
  { value: '0.1% - 0.5%', label: '0.1% - 0.5%' },
  { value: '0.5% - 1%', label: '0.5% - 1%' },
  { value: '1%-1.5%', label: '1%-1.5%' },
  { value: '1.5% - 2%', label: '1.5% - 2%' },
  { value: '2% - 5%', label: '2% - 5%' },
  { value: '5% - 10%', label: '5% - 10%' },
  { value: '10%+', label: '10%+' }
];

export const sortOrders = [
  { value: '', label: 'Updated Date' },
  { value: 'opened_at', label: 'Submitted Date' },
  { value: 'total_return', label: 'Total Return' },
  { value: 'return_vs_benchmark', label: 'Return vs. Benchmark' }
];

export const whitePapersSortOrders = [
  { value: '', label: 'Updated Date' },
  { value: 'opened_at', label: 'Submitted Date' }
];

export const seniority = labelize(['Senior', 'Subordinated', 'Junior Subordinated']);
